// TODO: Should be refactored 💯

import window from '@atlas/tool/box/browser/window';

import hideFloatingElement from './hideFloatingElement';

export default function showFloatingElement(
    el: HTMLElement,
    container: HTMLElement,
    options?: {
        horizontalOffset?: number;
        stickyContainer?: HTMLElement;
        refreshInterval?: number;
    },
) {
    if (!window) {
        console.error('This function can only be used in a browser environment.');
        return;
    }

    if (!el) return;

    const canvas = findAbsoluteParent(container) || window;
    const canvasPosition = canvas instanceof HTMLElement ? canvas.getBoundingClientRect() : { top: 0, left: 0 };

    const stickyContainer = options?.stickyContainer ?? container;
    const horizontalOffset = options?.horizontalOffset ?? 0;
    const refreshInterval = options?.refreshInterval ?? 1e3;

    try {
        const domSelection = window.getSelection();
        if (!domSelection) {
            hideFloatingElement(el);
            return;
        }

        if (!domSelection.anchorNode || domSelection.anchorNode?.constructor.name !== 'Text') return;

        const domRange = domSelection.getRangeAt(0);
        const selectedRect = domRange.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const initialScrollTop = stickyContainer.scrollTop;

        const positionSticky = () => {
            if (!stickyContainer) return;

            const delay = 0.1;
            const transition = el.style.transition;
            const newTransition = `top ${delay}s ease 0s, left ${delay}s ease 0s`;
            if (transition !== newTransition)
                el.style.transition = transition ? `${transition}, ${newTransition}` : newTransition;
            if (el.style.position !== 'fixed') el.style.position = 'fixed';

            const stickyRect = stickyContainer?.getBoundingClientRect();

            const selectionTopPosition = selectedRect.top + initialScrollTop - stickyContainer.scrollTop;
            const selectionBottomPosition = selectedRect.bottom + initialScrollTop - stickyContainer.scrollTop;

            const top = (() => {
                // Hide element if selection is not visible

                if (selectionTopPosition >= stickyRect.bottom || selectionBottomPosition <= stickyRect.top) {
                    return -1;
                }

                // Trying to fit inside of the container

                const topSelectionIsVisible =
                    selectionTopPosition >= stickyRect.top && selectionTopPosition <= stickyRect.bottom;

                const bottomSelectionIsVisible =
                    selectionBottomPosition >= stickyRect.top && selectionBottomPosition <= stickyRect.bottom;

                const positionAboveSelection = selectionTopPosition - el.offsetHeight;

                const elementFitsContainerAboveSelection =
                    topSelectionIsVisible && positionAboveSelection >= stickyRect.top;

                if (elementFitsContainerAboveSelection) {
                    return positionAboveSelection;
                }

                const positionBelowSelection = selectionBottomPosition;

                const elementFitsContainerBelowSelection =
                    bottomSelectionIsVisible && positionBelowSelection + el.offsetHeight <= stickyRect.bottom;

                if (elementFitsContainerBelowSelection) {
                    return positionBelowSelection;
                }

                // When element doesn't fit inside of the container next to the selection, try to stick it to selection outside of the container

                const elementFitsScreenAboveSelection = topSelectionIsVisible && positionAboveSelection >= 0;

                if (elementFitsScreenAboveSelection) {
                    return positionAboveSelection;
                }

                const elementFitsScreenBelowSelection =
                    bottomSelectionIsVisible && canvas && positionBelowSelection + el.offsetHeight <= getHeight(canvas);

                if (elementFitsScreenBelowSelection) {
                    return positionBelowSelection;
                }

                // Try to stick it to the container

                const positionAboveContainer = stickyRect.top - el.offsetHeight;

                const elementFitsScreenAboveContainer = positionAboveContainer >= 0;

                if (elementFitsScreenAboveContainer) {
                    return positionAboveContainer;
                }

                const positionBelowContainer = stickyRect.bottom;

                const elementFitsScreenBelowContainer =
                    canvas && positionBelowContainer + el.offsetHeight <= getHeight(canvas);

                if (elementFitsScreenBelowContainer) {
                    return positionBelowContainer;
                }

                // When it is to big to fit on the screen above and below the container, stick it to the top of the screen

                return 0;
            })();

            if (top === -1) {
                if (el.style.opacity !== '0') el.style.opacity = '0';
                if (el.style.top !== '-10000px') el.style.top = '-10000px';
            } else {
                if (el.style.opacity !== '1') el.style.opacity = '1';
                const newTop = `${top - canvasPosition.top}px`;
                if (el.style.top !== newTop) el.style.top = newTop;
            }

            const yScroll = canvas ? getYScroll(canvas) : 0;

            const left =
                selectedRect.left + yScroll - el.offsetWidth / 2 + selectedRect.width / 2 - (containerRect?.left || 0);
            const newLeft = `${
                (containerRect?.left || 0) +
                (left < horizontalOffset
                    ? horizontalOffset
                    : container
                    ? left > container.offsetWidth - el.offsetWidth - horizontalOffset
                        ? container.offsetWidth - el.offsetWidth - horizontalOffset
                        : left
                    : left) -
                canvasPosition.left
            }px`;

            if (el.style.left !== newLeft) el.style.left = newLeft;

            if (transition) {
                setTimeout(() => {
                    el.style.transition = transition;
                }, delay * 1e3);
            }
        };

        positionSticky();
        const handler = () => positionSticky();
        stickyContainer.addEventListener('scroll', handler);

        let timeout = 14;
        let timeoutId = setTimeout(function refreshPosition() {
            positionSticky();
            if (timeout < refreshInterval) {
                timeout *= 2;
                if (timeout > refreshInterval) timeout = refreshInterval;
            }

            timeoutId = setTimeout(refreshPosition, timeout);
        }, timeout);

        return () => {
            stickyContainer.removeEventListener('scroll', handler);
            clearTimeout(timeoutId);
        };
    } catch (error) {
        console.error(error);
    }
}

// #region Helpers

function findAbsoluteParent(element: HTMLElement): HTMLElement | undefined {
    const { parentElement } = element;
    if (!parentElement) return;
    const parentPosition = getComputedStyle(parentElement).position;
    if (parentPosition === 'absolute' || parentPosition === 'fixed') return parentElement;
    return findAbsoluteParent(parentElement);
}

function getYScroll(element: HTMLElement | Window) {
    return 'scrollY' in element ? element.scrollY : element.scrollTop;
}

function getHeight(element: HTMLElement | Window) {
    return 'innerHeight' in element ? element.innerHeight : element.scrollHeight;
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('showFloatingElement', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
